import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Venue, VenueFeatures } from '@lunit/shared-types';
import { api } from '@core/api/client';
import { getVenueFeatures } from '@core/api/subscriptions';
import { useBranding } from '@core/hooks/useBranding';

interface VenueContextType {
  venue: Venue | null;
  venueSlug: string;
  features: VenueFeatures | null;
  loading: boolean;
  error: string | null;
}

const VenueContext = createContext<VenueContextType | null>(null);

/**
 * Заведение + фичи + брендинг. Ядро НЕ знает, откуда взялся слаг —
 * его передаёт шелл (mini-app: Telegram start_param c ретраями; PWA: сегмент URL).
 */
export function VenueProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [venue, setVenue] = useState<Venue | null>(null);
  const [features, setFeatures] = useState<VenueFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { applyBranding } = useBranding();

  useEffect(() => {
    if (!slug) {
      setError('Укажите заведение в URL. Например: /melman');
      setLoading(false);
      return;
    }

    void loadVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadVenue = async () => {
    try {
      setLoading(true);
      const data = await api.get<Venue>(`/venues/${slug}`);
      setVenue(data);

      if (data.branding) {
        applyBranding(data.branding);
      }

      const featuresData = await getVenueFeatures(data.id);
      setFeatures(featuresData);
      setError(null);

      // Последнее валидное заведение — для start_url '/' установленной PWA
      // (мульти-tenant: слаг в манифест не хардкодим). В mini-app безвредно.
      try {
        localStorage.setItem('pwa:lastVenueSlug', slug);
      } catch {
        // localStorage может быть недоступен — не критично
      }
    } catch (err) {
      console.error('Failed to load venue:', err);
      setError('Не удалось загрузить данные заведения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <VenueContext.Provider value={{ venue, venueSlug: slug, features, loading, error }}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenue() {
  const context = useContext(VenueContext);
  if (!context) {
    throw new Error('useVenue must be used within VenueProvider');
  }
  return context;
}
